/**
 * Presentation vocabulary for the four employee-fact sections.
 *
 * ---------------------------------------------------------------------
 * WHAT THIS FILE IS ALLOWED TO DO
 *
 * Turn an enum code into the words a reader expects, and re-arrange a date
 * string the API already produced. That is all.
 *
 * It computes nothing. There is no proficiency-to-number mapping here, no
 * ordering of achievements by importance, no summed experience total, and
 * no "is this credential valid" verdict — every one of those would be a
 * domain conclusion invented in the browser, and the domain layer
 * deliberately declines to draw them (docs/DERIVED_LOGIC.md §19a).
 *
 * In particular, note what is absent: `SKILL_PROFICIENCY_ORDER` exists in
 * the domain (domain/src/models/common.ts) because Role Fit needs to
 * compare a proficiency against a threshold. It is not re-implemented here.
 * Ranking four employees' proficiencies against each other, or plotting a
 * proficiency on the attributes' 1-20 scale, are things this UI must not
 * do, and the easiest way to guarantee that is to give it no ordinal to do
 * it with.
 * ---------------------------------------------------------------------
 */

import type { QualificationVerificationStatus, SkillProficiencyLevel } from '../../api/types';
import { formatCalendarDate } from './attributeDisplay';

/**
 * `formatCalendarDate` (`2019-04-02` → `2 Apr 2019`) is re-exported from
 * `attributeDisplay` rather than reimplemented here: it re-arranges the
 * string the API already produced and never constructs a `Date`, and a
 * second copy would be a second chance to reintroduce the timezone shift
 * Mission 008's `api/src/dto/dates.ts` removed.
 */
export { formatCalendarDate } from './attributeDisplay';

/**
 * The four `skill_proficiency_level` values, in the reader's words.
 *
 * `meaning` is intentionally about *what was recorded*, not about how good
 * the person is. A skill record is a stated current proficiency with one
 * author — unlike an attribute, which is a weighted composite of several
 * sources with a confidence attached (docs/DOMAIN_MODEL.md §2.7). Wording
 * them as levels of a person rather than levels of a record would quietly
 * turn a fact into an assessment.
 */
export const PROFICIENCY_COPY: Record<SkillProficiencyLevel, { label: string; meaning: string }> = {
  beginner: { label: 'Beginner', meaning: 'Recorded as beginner proficiency.' },
  intermediate: { label: 'Intermediate', meaning: 'Recorded as intermediate proficiency.' },
  advanced: { label: 'Advanced', meaning: 'Recorded as advanced proficiency.' },
  expert: { label: 'Expert', meaning: 'Recorded as expert proficiency.' },
};

/**
 * `qualification_verification_status`, worded so that "unverified" reads as
 * *nobody has checked this yet* rather than as *this is doubtful*. The
 * distinction matters: an unverified credential is an administrative state,
 * not an accusation.
 */
export const VERIFICATION_COPY: Record<QualificationVerificationStatus, { label: string; meaning: string }> = {
  unverified: { label: 'Unverified', meaning: 'Nobody has checked this record against the issuing body.' },
  pending: { label: 'Verification pending', meaning: 'A check against the issuing body is in progress.' },
  verified: { label: 'Verified', meaning: 'Checked against the issuing body.' },
};

/**
 * A recorded number of years, as recorded.
 *
 * No rounding to a "sensible" figure and no unit conversion — 7.4 recorded
 * years is displayed as 7.4, because the stored value is the fact and a
 * tidier number would be a different claim. Durations are never added
 * together anywhere in this UI (see EmployeeExperienceResponse).
 */
export function formatYears(value: number | null, absent = 'Not recorded'): string {
  if (value === null) return absent;
  return `${value} ${value === 1 ? 'year' : 'years'}`;
}

/**
 * The period one experience record covers, exactly as far as the record
 * goes. An open-ended record says so; it does not silently become
 * "until today", which would assert something nobody recorded.
 */
export function formatPeriod(startedOn: string | null, endedOn: string | null): string {
  if (startedOn === null && endedOn === null) return 'Period not recorded';
  const start = startedOn === null ? 'Start not recorded' : formatCalendarDate(startedOn);
  const end = endedOn === null ? 'no recorded end' : formatCalendarDate(endedOn);
  return `${start} — ${end}`;
}
